import React, { useContext } from 'react';
import { PropTypes } from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import ReactTooltip from 'react-tooltip';

// Components
import HeroDropdown from '../../sections/HeroDropdown';

// Functionality
import { AttendeeModeContext, SearchModeContext } from '../../../context/searchContext';
import { SearchTypeContext } from '../../../context/searchTypeContext';
import searchTypes from '../../../data/searchTypeOptions';
import colors from '@general/colors';
import useAuthorized from '@hooks/useAuthorized';

const SearchTypeToggle = ({ onSearchTypeChange }) => {
    const [searchType, setSearchType] = useContext(SearchTypeContext);
    const { isMemberActive, isLoggedIn } = useAuthorized();
    const { isMemberCompanySearch, isPublicationsSearch } = useContext(SearchModeContext);
    const { isAttendeeSearch } = useContext(AttendeeModeContext);

    const getLabel = option => {
        if (!option.authRequired || (option.authRequired && isMemberActive)) {
            return option.optionLabel;
        }
        return (
            <span style={{ opacity: '.5' }}>
                {option.optionLabel}
                <FontAwesomeIcon icon={faLock} size="xs" style={{ marginLeft: '5px' }} />
            </span>
        );
    };

    const getSearchTypes = () => {
        if (isPublicationsSearch) {
            return [];
        }

        if (isMemberCompanySearch) {
            return [searchTypes.find(option => option.index === 'members')];
        }

        if (isAttendeeSearch) {
            // if attendee search, hide all but members and companies. Also relabel members to attendees
            // do not show auth because that happens in template
            return searchTypes.map(type => ({
                ...type,
                optionLabel: type.index === 'members' ? 'Attendees' : type.optionLabel,
                hidden: type.index !== 'member_events' && type.index !== 'companies',
            })).sort((a, b) => a.optionLabel < b.optionLabel ?  -1 : 1);
        }

        // add lock to search types that have auth
        const searchTypesWithAuth = searchTypes
            .filter(s => !s.hideOnMainSearch)
            .map(type => ({
                ...type,
                optionLabel: getLabel(type),
            }));

        return searchTypesWithAuth;
    };

    const finalSearchTypes = getSearchTypes();

    const renderLabel = (option, i) => {
        if (!option.authRequired || (option.authRequired && isMemberActive)) {
            return option.optionLabel;
        }

        const { optionLabel } = option;
        const id = `tab-${i}`;
        return (
            <>
                <span data-tip data-for={id}>
                    {optionLabel}
                </span>
                <ReactTooltip id={id} type="light" effect="solid">
                    <div style={{ maxWidth: '220px', textAlign: 'center' }}>
                        {!isLoggedIn && 'You must login and have an active membership to view this content.'}
                        {isLoggedIn && !isMemberActive && 'You must have an active membership to view this content.'}
                    </div>
                </ReactTooltip>
            </>
        );
    };

    return (
        <>
            <ul className="hidden-sm-down" style={styles.searchTypeList}>
                {finalSearchTypes.map((option, i) => {
                    const { id, index } = option;

                    return (
                        <li key={index} className={option.hidden ? 'd-none' : ''} style={styles.searchTypeListItem}>
                            <h5 style={{ fontFamily: 'Red Hat Display', marginBottom: 0 }}>
                                <a
                                    style={styles.searchTypeLink(searchType.index === index)}
                                    tabIndex={i}
                                    role="button"
                                    onKeyDown={() => {
                                        setSearchType(option);
                                        onSearchTypeChange(option);
                                    }}
                                    onClick={() => {
                                        setSearchType(option);
                                        onSearchTypeChange(option);
                                    }}
                                >
                                    {renderLabel(option, i)}
                                </a>
                            </h5>
                        </li>
                    );
                })}
            </ul>

            <div className="hidden-md-up">
                <HeroDropdown
                    options={finalSearchTypes}
                    selectedOption={searchType}
                    onOptionSelect={selectedOption => {
                        setSearchType(selectedOption);
                        onSearchTypeChange(selectedOption);
                    }}
                />
            </div>
        </>
    );
};

const styles = {
    searchTypeList: {
        listType: 'none',
        marginBottom: '-3px',
        paddingBottom: 0,
        paddingLeft: 0,
    },
    searchTypeListItem: {
        color: colors.white,
        display: 'inline-block',
        fontFamily: 'Red Hat Display',
        fontWeight: 700,
        marginRight: '25px',
    },
    searchTypeLink: selected => {
        const searchTypeLinkStyles = {
            color: colors.white,
            paddingBottom: '12px',
            display: 'block',
        };

        if (selected) {
            searchTypeLinkStyles.borderBottom = `3px solid ${colors.primary}`;
        }

        if (!selected) {
            searchTypeLinkStyles.cursor = 'pointer';
        }

        return searchTypeLinkStyles;
    },
};

SearchTypeToggle.propTypes = {
    onSearchTypeChange: PropTypes.func,
};

export default SearchTypeToggle;
